import { UploadAssetsURL, UpdateUploadStatusURL } from "../components/Dam/DAM_API/urls";
import Axios from "./Axios";

/* ----------------------------------
   SPLIT A DROP INTO ONE REQUEST PER ASSET / FOLDER

   The server issues one activityId per request, and that id is what the
   progress panel and the conflict popup key off. So a folder goes up as a
   single request (it is one thing to the user) and every loose asset gets a
   request of its own.

   A folder is recognised by the dropzone's relative path carrying a segment —
   "MyFolder/sub/a.png" belongs to MyFolder, while a file dropped on its own has
   no segment and stands alone.
---------------------------------- */

export const splitUploadRequests = (files = [], getPath = (f) => f?.path) => {
    const groups = new Map();

    files.forEach((f, index) => {
        const relativePath = (getPath(f) || "").replace(/^(\.\/|\/)/, "");
        const folderName = relativePath.includes("/") ? relativePath.split("/")[0] : "";

        // loose assets are keyed by position so no two ever share a request
        const key = folderName || `asset:${index}`;

        if (!groups.has(key)) groups.set(key, { folderName, files: [] });

        groups.get(key).files.push(f);
    });

    return [...groups.values()];
};

/* ----------------------------------
   RUN UPLOAD REQUESTS WITH A CEILING

   One request per asset means a large drop is a large number of requests.
   Browsers already cap concurrent connections per host at around six, so this
   costs nothing in throughput — it just stops hundreds of XHRs (and the file
   handles they hold) from piling up at once.

   Takes thunks, and returns Promise.allSettled-shaped results in input order.
---------------------------------- */

const UPLOAD_CONCURRENCY = 6;

export const runUploadRequests = async (tasks = [], limit = UPLOAD_CONCURRENCY) => {
    const results = [];
    let cursor = 0;

    const worker = async () => {
        while (cursor < tasks.length) {
            const index = cursor++;

            try {
                results[index] = { status: "fulfilled", value: await tasks[index]() };
            } catch (reason) {
                results[index] = { status: "rejected", reason };
            }
        }
    };

    await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));

    return results;
};

export const startUploadAssets = async (formData, onProgress , uploadurl, activityId) => {
    if (typeof window === "undefined") {
        throw new Error("Upload must run in browser");
    }

    const response = await Axios.post(
       uploadurl? uploadurl : UploadAssetsURL,
        formData,
        {
            // the id this upload goes by — the server keys its socket events by it
            params: activityId ? { activityId } : undefined,
            headers: {
                "Content-Type": "multipart/form-data",
            },
            // Old version
            progress(progressEvent) {
                console.log({ progressEvent });
            },
            // New version
            onUploadProgress: (e) => {
                if (!e.total) return;
                const percent = Math.round((e.loaded * 100) / e.total);
                onProgress?.(percent);
            },
        }
    );

    if (response.status === 202) {
        return { status: "PREPARING", uploadId: response.data.data };
    }

    if (response.status === 200) {
        return { status: "DONE", uploadId: response.data.data };
    }

    throw new Error("Upload failed");
};

/* ----------------------------------
   RESOLVE A DUPLICATE-NAME UPLOAD

   The server parks an asset whose name already exists in the target folder and
   raises a CONFLICT frame. This tells it what the user decided.

   Payload mirrors the socket frame — message / uploadId / existingId /
   deviceId / activityId all come straight back off it. Only `status` is new:
   it carries the user's choice.
---------------------------------- */

export const updateUploadStatus = async (payload, apiURL) => {
    const response = await Axios.post(apiURL || UpdateUploadStatusURL, payload);

    return response?.data;
};
